/* eslint-disable react-refresh/only-export-components */
import "@formatjs/intl-relativetimeformat/polyfill";
import "@formatjs/intl-relativetimeformat/locale-data/en";
import "@formatjs/intl-relativetimeformat/locale-data/de";
import "@formatjs/intl-relativetimeformat/locale-data/es";
import "@formatjs/intl-relativetimeformat/locale-data/fr";
import "@formatjs/intl-relativetimeformat/locale-data/ja";
import "@formatjs/intl-relativetimeformat/locale-data/zh";

import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { IntlProvider } from "react-intl";

import {
  I18N_CONFIG_KEY,
  hydrateLanguage,
  serializeLanguageForStorage,
} from "@/i18n/config";
import { type TLanguage, type ITranslationProviderProps } from "@/i18n/types.d";
import { getData, setData } from "@/utils";
import { store } from "@/store/store";
import { setLanguage } from "@/store/authSlice";

const getInitialLanguage = () => {
  const stored = getData(I18N_CONFIG_KEY) as TLanguage | undefined;
  return hydrateLanguage(stored);
};

const initialProps: ITranslationProviderProps = {
  currentLanguage: getInitialLanguage(),
  changeLanguage: () => {},
  isRTL: () => false,
};

const TranslationsContext =
  createContext<ITranslationProviderProps>(initialProps);
const useLanguage = () => useContext(TranslationsContext);

const I18NProvider = ({ children }: PropsWithChildren) => {
  const { currentLanguage } = useLanguage();

  return (
    <IntlProvider
      messages={currentLanguage.messages}
      locale={currentLanguage.code}
      defaultLocale={hydrateLanguage().code}
    >
      {children}
    </IntlProvider>
  );
};

const TranslationProvider = ({ children }: PropsWithChildren) => {
  const [currentLanguage, setCurrentLanguage] = useState(
    initialProps.currentLanguage,
  );

  const changeLanguage = (language: TLanguage) => {
    const hydrated = hydrateLanguage(language);
    setData(I18N_CONFIG_KEY, serializeLanguageForStorage(hydrated));
    setCurrentLanguage(hydrated);
    store.dispatch(setLanguage(hydrated.code));
  };

  const isRTL = () => {
    return currentLanguage.direction === "rtl";
  };

  useEffect(() => {
    document.documentElement.setAttribute("dir", currentLanguage.direction);
  }, [currentLanguage]);

  return (
    <TranslationsContext.Provider
      value={{
        isRTL,
        currentLanguage,
        changeLanguage,
      }}
    >
      <I18NProvider>{children}</I18NProvider>
    </TranslationsContext.Provider>
  );
};

export { TranslationProvider, useLanguage };
